import { useTranslation } from "react-i18next";

import { Paper, Switch } from "@mui/material";
import { ContractCoverage } from "../../../redux/contracts/contracts-types";
import {
    TableCell,
    TableContainer,
    TableRow,
    Table,
    TableHead,
    TableHeadCell,
    TableBody,
} from "../../../components/Table";
import { displayDateTime } from "../../../utils/utils";
import React from "react";
import { ContractStateCell } from "../../contracts/ContractStateCell";

export type Row = {
    /**
     * Table-unique key for this row (for React).
     */
    key: number;

    /**
     * Type of the contract (translated), such as "Company DPA".
     */
    type: string;

    /**
     * Current contract coverage - what is used to fulfill coverage for this specific contract (if anything)
     */
    contractCoverage: ContractCoverage;

    /**
     * Whether the framework agreement is toggled on or off.
     */

    frameworkAgreementOn: boolean;

    /**
     * What to do when the framework agreement switch toggle is clicked.
     * @param toggle
     */
    switchAction: (toggle: boolean) => void;
};

export type Props = {
    /**
     * Rows to be displayed in the table.
     */
    rows: Row[];
};

const ContractsRow: React.FC<Row> = ({ key, type, contractCoverage, frameworkAgreementOn, switchAction }) => {
    const updatedAt = contractCoverage.coveredByFramework?.updatedAt || contractCoverage.contract?.createdAt || null;
    const formattedUpdateString = updatedAt ? displayDateTime(new Date(updatedAt)) : "-";

    return (
        <TableRow key={key}>
            <TableCell>{type}</TableCell>
            <ContractStateCell contractCoverage={contractCoverage} disableTooltip={true} />
            <TableCell>{formattedUpdateString}</TableCell>
            <Switch
                checked={frameworkAgreementOn}
                onChange={(event) => {
                    switchAction(event.target.checked);
                }}
            />
        </TableRow>
    );
};

/**
 * Table that displays different company contract requirements and their status.
 * It includes a switch to toggle the framework contract on and off.
 */
export const CompanyContractsTable: React.FC<Props> = ({ rows }) => {
    const { t } = useTranslation();

    return (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableHeadCell>{t("contracts.table.header.contractType")}</TableHeadCell>
                        <TableHeadCell>{t("contracts.table.header.state")}</TableHeadCell>
                        <TableHeadCell>{t("contracts.table.header.updatedAt")}</TableHeadCell>
                        <TableHeadCell>{t("contracts.table.header.framework")}</TableHeadCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <ContractsRow
                            key={row.key}
                            type={row.type}
                            contractCoverage={row.contractCoverage}
                            frameworkAgreementOn={row.frameworkAgreementOn}
                            switchAction={row.switchAction}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
